/**
 * FIE Audit Log — Screen 5
 * Expanded action types for full coverage.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, FileText } from "lucide-react";
import { useFIE } from "@/data/fie/context";
import { useState } from "react";
import type { FIEAuditAction } from "@/data/fie/types";

const ACTION_LABELS: Record<FIEAuditAction, string> = {
  assumption_changed: "Assumption Changed",
  protected_assumption_changed: "Protected Assumption Changed",
  ownership_changed: "Ownership Changed",
  period_reconciled: "Period Reconciled",
  forecast_generated: "Forecast Generated",
  access_granted: "Access Granted",
  access_revoked: "Access Revoked",
  assumption_created: "Assumption Created",
  assumption_snapshot_created: "Snapshot Created (Manual)",
  snapshot_auto_created: "Snapshot Created (Auto)",
  period_locked: "Period Locked",
  distribution_viewed: "Distribution Viewed",
  restricted_access_attempt: "Restricted Access Attempt",
  integrity_mismatch: "Integrity Mismatch",
};

const ACTION_COLORS: Record<FIEAuditAction, string> = {
  assumption_changed: "bg-warning/10 text-warning border-warning/20",
  protected_assumption_changed: "bg-destructive/10 text-destructive border-destructive/20",
  ownership_changed: "bg-primary/10 text-primary border-primary/20",
  period_reconciled: "bg-success/10 text-success border-success/20",
  forecast_generated: "bg-info/10 text-info border-info/20",
  access_granted: "bg-success/10 text-success border-success/20",
  access_revoked: "bg-destructive/10 text-destructive border-destructive/20",
  assumption_created: "bg-info/10 text-info border-info/20",
  assumption_snapshot_created: "bg-primary/10 text-primary border-primary/20",
  snapshot_auto_created: "bg-primary/10 text-primary border-primary/20",
  period_locked: "bg-warning/10 text-warning border-warning/20",
  distribution_viewed: "bg-info/10 text-info border-info/20",
  restricted_access_attempt: "bg-destructive/10 text-destructive border-destructive/20",
  integrity_mismatch: "bg-destructive/10 text-destructive border-destructive/20",
};

export function FIEAuditLogView() {
  const { auditLog } = useFIE();
  const [filter, setFilter] = useState("all");

  const filteredLog = filter === "all" ? auditLog : auditLog.filter(e => e.action === filter);
  const actionTypes = [...new Set(auditLog.map(e => e.action))];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Audit Log</h1>
        <p className="text-sm text-muted-foreground">
          All changes to sensitive financial assumptions, access events, and governance actions
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Filter by action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {actionTypes.map(a => (
              <SelectItem key={a} value={a}>{ACTION_LABELS[a as FIEAuditAction] || a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant="secondary">{filteredLog.length} entries</Badge>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Object</TableHead>
                <TableHead>Old Value</TableHead>
                <TableHead>New Value</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Period</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLog.map(e => (
                <TableRow key={e.id} className={e.action === "restricted_access_attempt" ? "bg-destructive/5" : ""}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(e.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{e.userName}</div>
                    <div className="text-[10px] text-muted-foreground">{e.userRole}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${ACTION_COLORS[e.action as FIEAuditAction] || ""}`}>
                      {ACTION_LABELS[e.action as FIEAuditAction] || e.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{e.objectLabel}</TableCell>
                  <TableCell className="font-mono text-xs text-destructive">{e.oldValue || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-success">{e.newValue || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{e.reason}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.periodId || e.effectiveDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
